import { useState, type FC } from 'react';
import { Popover, Spin, Upload, message } from 'antd';
import { Paperclip, Trash2, Upload as UploadIcon } from 'lucide-react';
import type { OpportunityAttachmentVO } from '../api/ipc/opportunity';

export type OpportunityAttachmentRow = {
  attachmentCount?: number;
  attachments?: OpportunityAttachmentVO[];
};

type Props = {
  row: OpportunityAttachmentRow;
  /** 可编辑：弹层内支持上传 / 删除 */
  editable?: boolean;
  onChange?: (next: OpportunityAttachmentVO[]) => void;
  onUploadFiles?: (files: File[]) => Promise<OpportunityAttachmentVO[]>;
};

/** 机会列表/引入弹窗/Launch Plan 共用：附件数量悬停可下载；可编辑时支持上传删除。 */
export const OpportunityAttachmentCell: FC<Props> = ({
  row,
  editable = false,
  onChange,
  onUploadFiles,
}) => {
  const list = row.attachments || [];
  const count = row.attachmentCount ?? list.length;
  const [uploading, setUploading] = useState(false);

  if (!editable && count <= 0) return null;

  const removeAt = (idx: number) => {
    if (!onChange) return;
    onChange(list.filter((_, i) => i !== idx));
  };

  const handleUpload = async (files: File[]) => {
    if (!files.length || !onUploadFiles || !onChange) return;
    setUploading(true);
    try {
      const added = await onUploadFiles(files);
      if (added.length) onChange([...list, ...added]);
    } catch (e: any) {
      message.error(e?.message || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const content = (
    <div className={`opp-attachment-popover${editable ? ' opp-attachment-popover--editable' : ''}`}>
      {list.length === 0 ? (
        <div className="opp-attachment-popover__empty">暂无附件</div>
      ) : (
        <ul className="opp-attachment-popover__list">
          {list.map((a, idx) => {
            const href = a.downloadUrl || a.fileUrl || '';
            return (
              <li key={a.id || a.fileId || `${a.fileName}-${idx}`} className="opp-attachment-popover__row">
                <Paperclip size={12} className="opp-attachment-popover__icon" />
                <a
                  className="opp-attachment-popover__item"
                  href={href || '#'}
                  target="_blank"
                  rel="noreferrer"
                  onClick={e => {
                    if (!href) {
                      e.preventDefault();
                      message.warning('暂无下载地址');
                    }
                  }}
                >
                  {a.fileName || `文件 ${a.fileId || idx + 1}`}
                </a>
                {editable && (
                  <button
                    type="button"
                    className="opp-attachment-popover__remove"
                    title="删除"
                    aria-label="删除附件"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeAt(idx);
                    }}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {editable && (
        <div className="opp-attachment-popover__actions">
          <Upload
            multiple
            showUploadList={false}
            disabled={uploading || !onUploadFiles}
            beforeUpload={(file, fileList) => {
              if (fileList[0] === file) {
                void handleUpload(fileList as unknown as File[]);
              }
              return false;
            }}
          >
            <button type="button" className="opp-attachment-popover__upload" disabled={uploading}>
              {uploading ? <Spin size="small" /> : <UploadIcon size={13} />}
              <span>{uploading ? '上传中…' : '上传附件'}</span>
            </button>
          </Upload>
        </div>
      )}
    </div>
  );

  return (
    <Popover
      trigger={editable ? ['hover', 'click'] : 'hover'}
      placement="bottomLeft"
      mouseEnterDelay={0.12}
      mouseLeaveDelay={editable ? 0.45 : 0.1}
      content={content}
    >
      <span className={`opp-attachment-count${editable ? ' opp-attachment-count--editable' : ''}`}>
        {count}
      </span>
    </Popover>
  );
};
